import { ChevronRightIcon } from '@chakra-ui/icons';
import CheckBoxTile from '@/components/atoms/CheckBoxTile';
import SvgIcon from '@/components/atoms/SvgIcon';
import iconsPng from '@/assets/img/png/icons';
import svgIcons from '@/assets/svg/icons';
import colors from '@/theme/colors';
import { Button, HStack, Image, Text, VStack } from '@chakra-ui/react';
import { ComponentTypes, NodeType } from '@/providers/DocumentUiProvider/types';
import useRenderJson from '@/providers/DocumentUiProvider/useRenderJson';
import { format } from 'date-fns';
import { useEffect, useState } from 'react';
import { useSuspenseQuery } from '@tanstack/react-query';
import CustomButton from '@/components/atoms/CustomButton';
import { getContentPolicyQuery } from '@/queries/legal';
import { extra } from '@/theme/colors/palettes';
import useSubmitContentCreatorApplication from '@/hooks/contentcreator/useSubmitContentCreatorApplication';
import useAcceptMultipleDocuments from '@/hooks/legal/useAcceptMultipleDocuments';
import { useCreatorApplicationStore } from '@/store/useCreatorApplicationStore';
import { useAppStore } from '@/store/useAppStore';
import { TSubmitContentCreatorApplicationBody } from '@/services/api/requests/contentCreatorApplications';
import { useAuthContext } from '@/context/auth/useAuthContext';
import { useNavigate } from '@tanstack/react-router';
import { contentCreatorRoutes } from '@/constants/routes/app';
import { buildCreatorSocialLinks } from './creatorSocialLinks';
import { getUnacceptedContentPolicy } from '@/utils/onboardingUtils';

const CreatorApplicationContentPolicy = () => {
  const [acceptedContentPolicy, setAcceptedContentPolicy] = useState(false);
  const [isApplicationSubmitted, setIsApplicationSubmitted] = useState(false);
  const [isPolicyExpanded, setIsPolicyExpanded] = useState(false);
  const { isAuthenticated, isContentCreator, signOut } = useAuthContext();
  const navigate = useNavigate();
  const previewData = useCreatorApplicationStore((state) => state.previewData);
  const publicCredentials = useCreatorApplicationStore((state) => state.publicCredentials);
  const resetCreatorApplicationStore = useCreatorApplicationStore((state) => state.reset);
  const legalAcceptanceStatuses = useAppStore((state) => state.legalAcceptanceStatuses);
  const pendingContentPolicy = getUnacceptedContentPolicy(legalAcceptanceStatuses);
  const isPolicyAcceptanceMode = !previewData && isAuthenticated && isContentCreator;

  const { data } = useSuspenseQuery(getContentPolicyQuery());
  const view = useRenderJson();

  const component: NodeType = { type: ComponentTypes.Markdown, children: data?.content };

  const { isPending: isSubmittingApplication, mutate: submitContentCreatorApplication } =
    useSubmitContentCreatorApplication(() => {
      resetCreatorApplicationStore();

      if (isAuthenticated) {
        navigate({ to: contentCreatorRoutes.creatorProfile });
        return;
      }

      setIsApplicationSubmitted(true);
    });
  const { isPending: isAcceptingContentPolicy, mutate: acceptContentPolicy } = useAcceptMultipleDocuments(() => {
    navigate({ to: contentCreatorRoutes.creatorProfile });
  });

  const [timestamp, setTimestamp] = useState('');

  useEffect(() => {
    setTimestamp(format(new Date(), 'MMM do yyyy, h:mm:ss a'));
  }, []);

  const submitApplication = () => {
    if (!previewData) return;

    const applicationBody: TSubmitContentCreatorApplicationBody = {
      bio: previewData.aboutMe,
      creatorName: previewData.creatorName?.trim() || undefined,
      keepNameSameAsRealName: previewData.keepNameSameAsRealName ?? true,
      keepPhotoSameAsMemberPhoto: isAuthenticated && Boolean(previewData.keepPhotoSameAsMemberPhoto),
      location: {
        city: previewData.city,
        state: previewData.state,
        zip: previewData.zipCode,
      },
      certifications: previewData.qualifications,
      contentFocus: previewData.contentFocus,
      socialLinks: buildCreatorSocialLinks(previewData.socialMediaHandles),
      contentPolicy: { documentId: data._id, version: data.version },
    };

    submitContentCreatorApplication({
      applicationBody,
      isExistingUser: isAuthenticated,
      publicCredentials: isAuthenticated ? undefined : (publicCredentials ?? undefined),
    });
  };

  const submitContentPolicyAcceptance = () => {
    const documentId = pendingContentPolicy?._id ?? data._id;

    acceptContentPolicy([documentId]);
  };

  const submitAction = () => {
    if (isPolicyAcceptanceMode) {
      submitContentPolicyAcceptance();
      return;
    }

    submitApplication();
  };

  const isSubmitDisabled = !acceptedContentPolicy || (!previewData && !pendingContentPolicy);

  const handleBackToSignIn = () => {
    signOut();
    navigate({ to: '/sign-in' });
  };

  if (isApplicationSubmitted) {
    return (
      <VStack
        minHeight={'100vh'}
        width={'100vw'}
        justifyContent={{ base: 'center', md: 'flex-start' }}
        paddingY={'130px'}
        paddingX={'24px'}
        spacing={'24px'}
      >
        <Text variant={'loraTitle'} color={'white'} fontSize={'26px'} lineHeight={'30px'} fontWeight={'400'}>
          Application Submitted
        </Text>

        <Image alt="ok-hand" src={iconsPng.okHand} width={90} height={90} marginTop={'18px'} />

        <VStack spacing={'10px'}>
          <Text color={'white'} fontSize={'16px'} lineHeight={'23px'} fontWeight={'600'} textAlign={'center'}>
            Your profile is under review.
          </Text>
          <Text
            color={'white'}
            fontSize={'16px'}
            lineHeight={'23px'}
            fontWeight={'600'}
            opacity={0.7}
            textAlign={'center'}
          >
            You’ll be notified once approved (within 24 hours).
          </Text>
        </VStack>

        <VStack spacing={'8px'} marginY={'18px'} width={'360px'} alignItems={'flex-start'}>
          <HStack spacing={'12px'}>
            <SvgIcon
              iconPath={svgIcons.bellFilled}
              size={20}
              color={colors.extra.orange}
              strokeWidth={'10'}
              additionalSvgProps={{ fillRule: 'evenodd', clipRule: 'evenodd' }}
            />
            <Text color={'secondary.500'} fontSize={'16px'} lineHeight={'20px'} fontWeight={'700'}>
              We’ll notify you when:
            </Text>
          </HStack>
          <Text
            color={'white'}
            fontSize={'16px'}
            lineHeight={'20px'}
            fontWeight={'600'}
            opacity={0.7}
            marginLeft={'34px'}
          >
            Your profile is approved
          </Text>
        </VStack>

        <CustomButton
          label="Go to myomnia.health"
          iconAfterLabelSvgPath={svgIcons.rightArrow}
          iconAfterLabelSvgColor={extra.white}
          width={'320px'}
          iconAfterLabelSvgSize={12}
          iconAfterLabelSvgBoxHeight={14}
          iconAfterLabelSvgBoxWidth={14}
          backgroundColor={'primary.500'}
          labelColor="white"
          onClick={() => window.open('https://myomnia.health', '_blank', 'noopener,noreferrer')}
        />
      </VStack>
    );
  }

  return (
    <VStack
      minHeight={'100vh'}
      width={'100vw'}
      justifyContent={{ base: 'center', md: 'flex-start' }}
      paddingY={'130px'}
      paddingX={'24px'}
      spacing={'28px'}
    >
      <VStack spacing={'10px'}>
        <Text variant={'loraTitle'} color={'white'} fontSize={'26px'} lineHeight={'32px'} fontWeight={'400'}>
          MyOmnia Content Policy
        </Text>
        <Text
          color={'white'}
          fontSize={'16px'}
          lineHeight={'20px'}
          fontWeight={'600'}
          opacity={0.7}
          textAlign={'center'}
        >
          Please review and accept our creator terms
        </Text>
      </VStack>

      <VStack
        width={'100%'}
        maxWidth={'620px'}
        backgroundColor={'white'}
        borderRadius={'10px'}
        padding={'20px'}
        alignItems={'stretch'}
        boxShadow={'0px 2px 18px 0px #1A00B933'}
        marginTop={'22px'}
        cursor={'pointer'}
        onClick={() => setIsPolicyExpanded((currentValue) => !currentValue)}
      >
        <HStack width={'100%'} minHeight={'46px'} justifyContent={'space-between'}>
          <VStack alignItems={'flex-start'} spacing={'8px'}>
            <Text
              variant={'loraTitle'}
              color={'text.darkBlue'}
              fontSize={'20px'}
              lineHeight={'26px'}
              fontWeight={'400'}
            >
              Wholeness Content Policy
            </Text>
            <Text color={'background.blueGray'} fontSize={'14px'} lineHeight={'18px'} fontWeight={'600'}>
              Please review before accepting
            </Text>
          </VStack>
          <ChevronRightIcon
            color={'text.darkBlue'}
            boxSize={'28px'}
            transform={isPolicyExpanded ? 'rotate(90deg)' : 'rotate(0deg)'}
            transition={'0.2s'}
          />
        </HStack>

        {isPolicyExpanded && (
          <VStack
            marginTop={'20px'}
            paddingTop={'20px'}
            spacing={'12px'}
            borderTop={'1px solid'}
            borderColor={'background.lightBlue'}
            alignItems={'flex-start'}
          >
            {data && view(component)}
          </VStack>
        )}
      </VStack>

      <VStack
        width={'100%'}
        maxWidth={'620px'}
        backgroundColor={'white'}
        borderRadius={'8px'}
        padding={'18px 20px'}
        alignItems={'flex-start'}
      >
        <Text color={'background.blueGray'} fontSize={'12px'} lineHeight={'16px'} fontWeight={'700'}>
          By clicking "I Agree", you acknowledge that you have read, understood, and agree to be bound by these terms.
          Your acceptance will be recorded with a timestamp: {timestamp}.
        </Text>
      </VStack>

      <CheckBoxTile
        checkboxOption={{ label: 'I agree with MyOmnia Content Policy' }}
        isChecked={acceptedContentPolicy}
        onClick={() => setAcceptedContentPolicy((currentValue) => !currentValue)}
        checkboxWidth={'360px'}
        maxTextWidth={'290px'}
        fontSize={'14px'}
        borderWidth={0}
      />

      <Button
        variant={'continue'}
        width={'100%'}
        maxWidth={'360px'}
        marginTop={'36px'}
        isDisabled={isSubmitDisabled}
        disabled={isSubmitDisabled}
        isLoading={isSubmittingApplication || isAcceptingContentPolicy}
        onClick={submitAction}
      >
        {isPolicyAcceptanceMode ? 'I Accept' : 'Submit Application'}
      </Button>

      {isPolicyAcceptanceMode && (
        <Button variant={'ghost'} color={'white'} onClick={handleBackToSignIn}>
          Back to Sign In
        </Button>
      )}
    </VStack>
  );
};

export default CreatorApplicationContentPolicy;
